import { DrawingPreferencesClient } from './drawing-preferences';
import { FlightClient } from '../modes/doodle-flight/client';
import { BrushStudio } from './brush-studio';
import { DrawGuessClient } from '../modes/draw-guess/client';
import { RescueClient } from '../modes/relay-rescue/client';
import { LiveClient } from '../modes/live-draw/client';
import { GalleryClient } from './gallery';
import { DrawingExamplesClient } from './drawing-examples';
import { GAME_MODES, modeInfo, type ConversationSummary } from '../shared/modes';
import type { RescueView } from '../modes/relay-rescue/types';
import type { Player } from '../shared/account';
import type { SessionView, PublicTurn } from '../modes/draw-guess/types';
import { PALETTES, PALETTE_GROUPS, tierName } from '../shared/account';
import { type Platform, type Pointer, ApiError, requestId } from './platform';
import { Painter, C, T } from './paint';
import { viewport, localPointer, deviceRect } from './layout';

declare const __VERSION__: string;
import type { Screen } from './mode';

export class GameApp {
  flight: FlightClient;
  drawGuess: DrawGuessClient;
  rescue: RescueClient;
  live: LiveClient;
  gallery: GalleryClient;
  examples: DrawingExamplesClient;
  drawingSettings: DrawingPreferencesClient;
  studio: BrushStudio;
  liveRoomActive() { return !!this.live.room; }
  liveToolStatus() { const r=this.live.room?.round; return r ? `${Math.max(0,Math.ceil((r.deadlineAt-Date.now())/1000))} 秒` : this.live.room ? '等候中' : null; }
  shopTier() { return this.drawGuess.shopTier; }
  setShopTier(value: number) { this.drawGuess.shopTier=value; }
  ctx: CanvasRenderingContext2D;
  paint: Painter;
  screen: Screen = 'welcome';
  token = '';
  player: Player | null = null;
  avatarImage: HTMLImageElement | null = null;
  avatarImageUrl = '';
  partnerAvatarImages = new Map<string, HTMLImageElement>();
  partnerAvatarLoading = new Set<string>();
  sessions: ConversationSummary[] = [];
  page = 0;
  busy = false;
  toastText = '';
  toastUntil = 0;
  lastPoll = 0;
  private pollPending = false;
  private refreshGeneration = 0;
  stopped = false;
  pendingInvite?: string;
  pendingRoom?: string;
  consented = false;
  privacyPage = 0;
  shopReturn: Screen = 'home';
  paletteSelection = { id: 'starter' };
  private paletteBuying = false;
  redeemCode = '';
  // —— 「纸上邮局」动效时间戳：全部是 Date.now() 起点，渲染循环内求值，弱机只掉帧不错位 ——
  /** 墨水转场：>0 时撕边墨布正在揭开新页面（仅顶层导航触发，流程内切屏不用）。 */
  wipeAt = 0;
  wipeSeed = 11;
  /** 转场方向：1 前进（左→右揭开），-1 后退（右→左揭开）。 */
  wipeDir: 1 | -1 = 1;
  /** 公共星星余额动效。 */
  coinsShown = -1;
  coinsAt = 0;
  /** 欢迎页进场（标题与信封动画起点）。 */
  bootAt = Date.now();
  get layout() { const metrics=this.screen==='flight'&&this.platform.menuRect ? {...this.platform,topInset:Math.max(this.platform.topInset,this.platform.menuRect.y+this.platform.menuRect.h+6),safeInsets:{...(this.platform.safeInsets??{top:0,left:0,right:0,bottom:0}),top:Math.max(this.platform.topInset,this.platform.menuRect.y+this.platform.menuRect.h+6)}}:this.platform;return viewport(metrics, ['settings', 'settings-profile', 'settings-redeem', 'privacy', 'session', 'live-create'].includes(this.screen)); }
  get W() { return this.layout.width; }
  get H() { return this.layout.height; }
  get top() { return this.layout.top; }
  get tablet() { return this.layout.tablet; }
  /** Horizontal anchors expand the page; type, icon sizes and vertical rhythm do not. */
  x(value: number) { return 24 + (value - 24) * (this.W - 48) / 342; }
  span(value: number) { return value * (this.W - 48) / 342; }
  toDevice(x: number, y: number) { const r = deviceRect({ x, y, w: 0, h: 0 }, this.layout); return { x: r.x, y: r.y }; }
  toast(message: string) { this.toastText = message; this.toastUntil = Date.now() + 3500; }
  /** 单次操作的兜底时限：超过它就不再让玩家干等。
   *  繁忙态是所有输入的总闸（控件 disabled、画布不落笔），只要有一个 await 永不落定，
   *  界面就等于假死、只能杀掉小游戏重进——真机上确实发生过（订阅提醒回调不触发）。
   *  所以即使某个平台接口漏了超时，这里也必须自己解锁。 */
  static readonly BUSY_TIMEOUT = 20_000;
  async run(fn: () => Promise<void>) {
    if (this.busy) return; this.busy = true;
    this.refreshGeneration++;
    let timedOut = false;
    try {
      // 手动管理计时器：race 里的 Promise 在超时后仍会落定，必须清掉定时器，否则会一直吊着事件循环。
      let timer: ReturnType<typeof setTimeout> | undefined;
      const watchdog = new Promise<void>(resolve => { timer = setTimeout(() => { timedOut = true; this.busy = false; resolve(); }, GameApp.BUSY_TIMEOUT); });
      try { await Promise.race([fn(), watchdog]); }
      finally { if (timer !== undefined) clearTimeout(timer); }
    }
    catch (error) {
      if (error instanceof ApiError && error.status === 401) { this.token = ''; this.player = null; this.screen = 'welcome'; }
      this.toast(error instanceof Error ? error.message : '出了点小问题，请重试。');
    } finally { this.busy = false; }
    // 先在 finally 之后判断：超时必须给出可读的提示，而不是让按钮「莫名其妙又能点了」。
    if (timedOut) this.toast('网络响应有点慢，这一步先取消了，请再试一次。');
  }
  api<T>(path: string, method = 'GET', body?: unknown, key?: string) { return this.platform.request<T>(path, method, body, this.token, method === 'GET' ? undefined : key ?? requestId()); }
  /** 顶层页面切换：换页瞬间盖上一块撕边墨布，下一帧起墨布扫开露出新页面（墨水转场）。
   *  dir=1 前进（墨布从左向右揭开），dir=-1 后退（从右向左）——方向感让用户知道自己在往哪走。
   *  流程内切屏（进画板 / 进猜词 / 揭晓结果）不用它——那些页面有自己的签名动效。 */
  go(screen: Screen, dir: 1 | -1 = 1) {
    if (this.screen === screen) return;
    this.refreshGeneration++;
    this.studio.picker.close();
    this.studio.shopScroll.cancel();
    if (this.screen === 'draw' || this.screen === 'guess') this.drawGuess.pause();
    if (this.screen === 'live-room') this.live.leaveCanvas();
    this.screen = screen; this.wipeAt = Date.now(); this.wipeDir = dir;
    this.wipeSeed = (this.wipeSeed * 16807 + 11) % 2147483647;
  }
  async signIn() {
    await this.run(async () => {
      if (!await this.platform.consent()) return;
      this.platform.set('consent-v1', true); this.consented = true;
      const auth = await this.platform.request<{ token: string; player: Player }>('/auth', 'POST', await this.platform.credentials());
      this.token = auth.token; this.player = auth.player; this.drawingSettings.bind();
      this.platform.set('flight-account',{player:auth.player,token:auth.token});
      // 首次登录直接落到首页，不播墨水转场——转场只留给登录后的顶层导航。
      this.screen = 'home'; await this.refresh();
      if (this.pendingRoom) { const code = this.pendingRoom; this.pendingRoom = undefined; await this.joinRoom(code); }
      else if (this.pendingInvite) await this.accept(this.pendingInvite);
      else await this.live.recover().catch(() => {});
    });
  }
  async refresh() {
    if (!this.player) return;
    this.refreshGeneration++;
    if (this.screen === 'rescue') await this.rescue.refresh();
    let pending: PublicTurn | undefined;
    if (this.drawGuess.session && ['session', 'guess'].includes(this.screen)) {
      this.drawGuess.session = await this.api(`/sessions/${this.drawGuess.session.id}`);
      if (this.screen === 'guess' && this.drawGuess.session?.current.status !== 'ready') this.screen = 'session';
      // 守在会话页时对方猜完了：先把猜题过程播出来，这就是「收到回音」的那一刻。
      if (this.screen === 'session') pending = this.drawGuess.pendingReplay();
    }
    const [player, list] = await Promise.all([this.api<Player>('/me'), this.api<{ sessions: ConversationSummary[] }>('/sessions')]);
    this.player = player; this.sessions = list.sessions; this.lastPoll = Date.now();
    this.loadAvatar(player.avatarUrl);
    this.loadPartnerAvatars(this.sessions);
    if (pending) await this.drawGuess.loadGuessReplay(pending, 'session');
  }
  /** 自动同步不占用输入锁；整批响应仍属于当前页面时才提交，旧响应不能回滚主动操作。 */
  async pollRefresh() {
    if (!this.player || this.busy || this.pollPending) return;
    // 救援刷新包含物理场景和自动回放切换，继续使用该模式原有的前台流程。
    if (this.screen === 'rescue') { this.lastPoll = Date.now(); await this.run(() => this.refresh()); return; }
    this.pollPending = true; this.lastPoll = Date.now();
    const generation = ++this.refreshGeneration; const token = this.token;
    let player = this.player; let screen = this.screen; let session = this.drawGuess.session;
    const turnId = session?.current.id;
    let active = true;
    const current = () => active && !this.stopped && !this.busy && this.refreshGeneration === generation &&
      this.token === token && this.player === player && this.screen === screen && this.drawGuess.session === session;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const sync = async () => {
        try {
          const [nextPlayer, list, nextSession] = await Promise.all([
            this.api<Player>('/me'), this.api<{ sessions: ConversationSummary[] }>('/sessions'),
            session && (screen === 'session' || screen === 'guess') ? this.api<SessionView>(`/sessions/${session.id}`) : undefined,
          ]);
          if (!current()) return;
          if (nextSession) {
            // ready 画作已寄出且不可编辑，沿用引用以保留已完成回放的离屏缓存。
            if (session?.current.id === nextSession.current.id && session.current.status === 'ready' && nextSession.current.status === 'ready' && session.current.drawing && nextSession.current.drawing) nextSession.current.drawing = session.current.drawing;
            this.drawGuess.session = session = nextSession;
            if (screen === 'guess' && (nextSession.status === 'closed' || nextSession.current.id !== turnId || nextSession.current.status !== 'ready')) this.screen = screen = 'session';
          }
          this.player = player = nextPlayer; this.sessions = list.sessions;
          this.loadAvatar(player.avatarUrl); this.loadPartnerAvatars(this.sessions);
          const pending = screen === 'session' ? this.drawGuess.pendingReplay() : undefined;
          if (pending) await this.drawGuess.loadGuessReplay(pending, 'session', current);
        } catch (error) {
          // 短暂断网保留当前画纸，下一次再同步；有效的登录过期仍提示重新登录。
          if (current() && error instanceof ApiError && error.status === 401) {
            this.token = ''; this.player = null; this.screen = 'welcome'; this.toast('登录已过期，请重新登录。');
          }
        }
      };
      const timeout = new Promise<void>(resolve => { timer = setTimeout(() => { active = false; resolve(); }, GameApp.BUSY_TIMEOUT); });
      await Promise.race([sync(), timeout]);
    } finally {
      active = false; if (timer !== undefined) clearTimeout(timer);
      this.pollPending = false; this.lastPoll = Date.now();
    }
  }
  loadAvatar(url?: string) {
    if (!url || !this.platform.loadImage || this.avatarImageUrl === url) return;
    this.avatarImageUrl = url; this.avatarImage = null;
    void this.platform.loadImage(url).then(image => { if (this.avatarImageUrl === url) this.avatarImage = image; }).catch(() => { /* Initial remains visible. */ });
  }
  home() {
    this.drawGuess.saveDraft(); this.rescue.pause(); this.page = 0;
    // 回首页是“后退”动作：墨布从右向左揭开。
    this.go(this.player ? 'home' : 'welcome', -1);
    if (this.player) void this.pollRefresh();
  }
  goBack() {
    if(this.screen==='flight'){this.flight.back();return;}
    if(this.studio.picker.active) { this.studio.picker.close(); return; }
    if(this.screen === 'brush-book') { this.studio.back(); return; }
    if(this.screen === 'shop' && this.studio.detail) { this.studio.backDetail(); return; }
    if (this.screen === 'drawing-example') { this.examples.openList(); return; }
    if (this.screen === 'drawing-examples') { this.go('settings', -1); return; }
    if (this.screen === 'live-room' || this.screen === 'live-create') { this.live.back(); return; }
    if (this.screen === 'artwork') { this.gallery.back(); return; }
    if (this.screen === 'rescue') { this.rescue.back(); return; }
    if (this.screen === 'modes') { this.home(); return; }
    if (this.screen === 'replay') { this.drawGuess.leaveProcess(); return; }
    if (this.screen === 'shop' && this.shopReturn !== 'shop') { this.go(this.shopReturn === 'live-room' && !this.live.room ? 'lobby' : this.shopReturn, -1); return; }
    // 设置的二级页返回设置主页；从设置进入的隐私说明也回到设置（未登录时回欢迎页）。
    if (this.screen === 'settings-profile' || this.screen === 'settings-redeem') { this.go('settings', -1); return; }
    if (this.screen === 'privacy' && this.player) { this.go('settings', -1); return; }
    this.home();
  }
  async joinRoom(code:string) { if(code.toUpperCase().startsWith('DF-'))await this.flight.join(code);else await this.live.join(code); }
  async accept(code: string) {
    this.pendingInvite = undefined;
    const cleaned = code.includes('invite=') ? (code.split('invite=')[1]?.split('&')[0] ?? '') : code;
    const view = await this.api<SessionView | RescueView>('/invites/accept', 'POST', { code: cleaned.trim().toUpperCase() });
    await this.openView(view); await this.refresh();
  }
  async openView(view: SessionView | RescueView) {
    if (view.mode === 'relay-rescue') await this.rescue.open(view);
    else await this.drawGuess.open(view);
  }
  openSession(id: string) { void this.run(async () => { await this.openView(await this.api<SessionView | RescueView>('/sessions/' + id)); }); }
  loadPartnerAvatars(sessions: ConversationSummary[]) {
    if (!this.platform.loadImage) return;
    for (const session of sessions) {
      const url = session.partnerAvatarUrl;
      if (!url || this.partnerAvatarImages.has(url) || this.partnerAvatarLoading.has(url)) continue;
      this.partnerAvatarLoading.add(url);
      void this.platform.loadImage(url).then(image => {
        this.partnerAvatarImages.set(url, image);
        this.partnerAvatarLoading.delete(url);
      }).catch(() => { this.partnerAvatarLoading.delete(url); });
    }
  }
  frame(now: number) {
    if (!this.stopped) {
      // 单帧异常不能让 rAF 链条断掉：否则界面会永久定格，玩家只能重启小游戏。
      try { this.drawingSettings.update(Date.now()); this.rescue.update(Date.now()); this.live.update(Date.now()); this.flight.update(Date.now()); this.render(Date.now()); }
      catch (error) { this.toast(error instanceof Error ? error.message : '画面出了点小问题，请重试。'); }
      if (this.player && this.pendingInvite && !this.busy) void this.run(() => this.accept(this.pendingInvite!));
      if (this.player && this.pendingRoom && !this.busy) { const code = this.pendingRoom; this.pendingRoom = undefined; void this.run(() => this.joinRoom(code)); }
      if (this.player && !this.busy && ['home', 'session', 'guess', 'settings', 'settings-profile', 'rescue'].includes(this.screen) && Date.now() - this.lastPoll > 12_000) void this.pollRefresh();
    }
    requestAnimationFrame(time => this.frame(time));
  }
  header(title: string, sub?: string, back = true) {
    const p = this.paint;
    if (back) { p.circle(p.x(40), 34, 17, C.cream, undefined, 8); p.text('‹', p.x(40), 33, 22, C.ink, '700', 'center'); p.hits.push({ id: 'back', label: '返回', x: p.x(16), y: 10, w: 48, h: 48, action: () => this.goBack() }); }
    if (!back && title === '友友画') {
      p.circle(p.x(42), 34, 17, C.accent, undefined, 5); p.icon('plane', p.x(42), 34, 15, C.white);
      // 品牌区只留标题 + 下方那句说明：原来的「一起画 · 一起猜 · 一起笑」按用户要求整句删除
      // （它正落在金色签名线占的那一行上，往上挤、往下空，怎么排都不自然，见 v0.0.107）。
      p.title('友友画', p.x(66), 32, T.logo);
    } else p.title(title, back ? p.x(68) : p.x(26), 34, 21);
    // 副标统一带一枚金色小星作项目符号，视觉锚定左缘。
    if (sub) { p.star(p.x(31), 80, 4, C.gold); p.text(sub, p.x(40), 80, T.caption, C.mute); }
  }
  /** 底部悬浮邮局柜台导航：图标 + 小字标签；命中 id 与布局保持原样（nav-home/gallery/shop/settings）。 */
  navigation(active: 'home' | 'lobby' | 'gallery' | 'settings' | 'shop') {
    const p = this.paint; const y = this.H - this.top - (this.tablet ? 78 : 70);
    const navWidth = this.tablet ? Math.min(600, this.W - 48) : 354;
    const navLeft = (this.W - navWidth) / 2, cell = (navWidth - 4) / 5;
    p.box(navLeft, y - 4, navWidth, this.tablet ? 64 : 58, C.cream, 29, undefined, 20);
    const tabs = [
      ['home', '友信', 'mail'], ['lobby', '大厅', 'people'], ['gallery', '画廊', 'frame'], ['shop', '商店', 'shop'], ['settings', '设置', 'gear'],
    ] as const;
    const activeIndex = tabs.findIndex(([page]) => page === active);
    tabs.forEach(([page, label, icon], i) => {
      const x = navLeft + 2 + i * cell; const on = active === page;
      if (on) p.box(x + 3, y + 3, cell - 6, this.tablet ? 50 : 42, C.tint, 21);
      const tint = on ? C.accentDeep : C.mute;
      p.icon(icon, x + cell / 2, y + 16, 17, tint);
      p.text(label, x + cell / 2, y + (this.tablet ? 38 : 35), 10.5, tint, '600', 'center');
      p.hits.push({
        id: `nav-${page}`, label, x, y: y + 3, w: cell, h: this.tablet ? 50 : 42, action: () => {
          // 导航栏按标签序决定转场方向：往右的页面向前揭，往左的页面（含最左的友信）向后揭。
          const dir = i >= activeIndex ? 1 : -1;
          if (page === 'gallery') this.gallery.navigate(dir);
          else if (page === 'lobby') this.live.lobby(dir);
          else if (page === 'home') this.home();
          else if (page === 'shop') { this.shopReturn = this.screen; this.page = 0; this.go('shop', dir); }
          else this.go('settings', dir);
        },
      });
    });
  }
  /** 星星罐：左置或右对齐的余额胶囊（永远避开右上角胶囊遮挡区）；余额变化时罐子弹跳。 */
  starJar(x: number, y: number, align: 'left' | 'right' = 'left') {
    const p = this.paint; const coins = this.player?.coins ?? 0;
    p.ctx.font = `700 13px "PingFang SC", "Microsoft YaHei", sans-serif`;
    const w = Math.ceil(p.ctx.measureText(`${coins}`).width) + 34;
    const x0 = align === 'right' ? x - w : x;
    const age = Date.now() - this.coinsAt; const pop = age < 420 ? 1 + 0.2 * Math.sin((age / 420) * Math.PI) : 1;
    p.ctx.save(); p.ctx.translate(x0 + w / 2, y + 15); p.ctx.scale(pop, pop); p.ctx.translate(-(x0 + w / 2), -(y + 15));
    p.box(x0, y, w, 30, C.tint, 15, C.accent, 8);
    p.star(x0 + 15, y + 15, 7.5, C.gold);
    p.text(`${coins}`, x0 + 26, y + 15.5, T.sub, C.accentDeep, '700');
    if (age < 420) { p.star(x0 + w - 2, y + 2, 4, C.gold); p.star(x0 - 2, y + 26, 3, C.gold); }
    p.ctx.restore();
    return w;
  }
  /** 环境纸飞机：欢迎页与首页背景里两只慢慢漂移的纸飞机，让静态页面有呼吸感。 */
  ambient(now: number) {
    const p = this.paint; const c = p.ctx;
    c.save(); c.globalAlpha = 0.07;
    for (let i = 0; i < 2; i++) {
      const speed = 11000 + i * 5000;
      const x = ((now / speed + i * 0.55) % 1) * 560 - 80;
      const y = 130 + i * 270 + Math.sin(now / 1700 + i * 2) * 14;
      p.plane(x, y, 0.1 + Math.sin(now / 2100 + i) * 0.06, 0.9, C.ink);
    }
    c.restore();
  }
  /** 墨水转场：撕边墨布沿 wipeDir 揭开新页面；400ms 缓动（先果断覆盖、后柔和揭示），
   *  前缘压一支画笔尖作为“刷开墨层”的表演；结束后 120ms 落版亮斑淡出，消除硬切台阶感。 */
  wipeNoise(i: number) { const v = Math.sin(this.wipeSeed * 12.9898 + i * 39.1165) * 43758.5453; return v - Math.floor(v); }
  wipeOverlay(now: number) {
    const age = now - this.wipeAt; if (age < 0) return;
    const c = this.ctx; const D = 400;
    // 落版软化：转场刚结束的 120ms 里一层极淡的纸色亮斑退掉。
    if (age >= D) {
      if (age < D + 120) {
        c.save(); c.globalAlpha = 0.06 * (1 - (age - D) / 120);
        c.fillStyle = C.cream; c.fillRect(0, 0, this.W, this.H); c.restore();
      }
      return;
    }
    const k = age / D;
    // easeInOutCubic：起步让眼睛来得及读墨、中段果断、收尾柔和。
    const ease = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
    const dir = this.wipeDir;
    const reveal = ease * (this.W + 240) - 130;
    const n = 26; const yAt = (i: number) => -10 + (this.H + 20) * i / n;
    const edge = (i: number) => dir === 1 ? reveal + (this.wipeNoise(i) - 0.5) * 34 : this.W - reveal - (this.wipeNoise(i) - 0.5) * 34;
    c.save();
    if (dir === 1 && reveal + 30 < 0 || dir === -1 && reveal + 30 < 0) { c.fillStyle = '#1F6CAE'; c.fillRect(0, 0, this.W, this.H); }
    else if (reveal - 60 < this.W) {
      // 已覆盖区域：dir=1 在撕边右侧，dir=-1 在撕边左侧。
      c.beginPath(); c.moveTo(edge(0), yAt(0));
      for (let i = 1; i <= n; i++) c.lineTo(edge(i), yAt(i));
      if (dir === 1) { c.lineTo(this.W + 10, this.H + 10); c.lineTo(this.W + 10, -10); }
      else { c.lineTo(-10, this.H + 10); c.lineTo(-10, -10); }
      c.closePath();
      const g = c.createLinearGradient(dir === 1 ? reveal - 120 : this.W - reveal, 0, dir === 1 ? reveal + 120 : this.W - reveal + 120, 0);
      g.addColorStop(0, '#4DA8E4'); g.addColorStop(1, '#1F6CAE');
      c.fillStyle = g; c.fill();
      // 撕边内侧的笔刷晕染带。
      c.beginPath(); c.moveTo(edge(0) - 48 * dir, yAt(0));
      for (let i = 1; i <= n; i++) c.lineTo(edge(i) - 48 * dir, yAt(i));
      for (let i = n; i >= 0; i--) c.lineTo(edge(i), yAt(i));
      c.closePath(); c.fillStyle = 'rgba(77, 168, 228, 0.32)'; c.fill();
      // 前缘的画笔尖：像有人正把墨刷开。
      const tipX = edge(13) - 14 * dir; const tipY = yAt(13) + (this.wipeNoise(3) - 0.5) * 30;
      c.save(); c.translate(tipX, tipY); c.rotate(dir === 1 ? -0.5 : 0.5);
      this.paint.icon('brush', 0, 0, 26, C.cream); c.restore();
    }
    c.restore();
  }
  /** 寄信纸飞机：提交成功后沿弧线飞出屏幕；作为浮层画在会话页之上。 */
  mailPlane(now: number) {
    const age = now - this.drawGuess.mailAt; if (age < 0 || age > 780) return;
    const p = this.paint; const c = this.ctx; const k = Math.min(1, age / 780); const e = 1 - Math.pow(1 - k, 2);
    const at = (t: number) => ({
      x: (1 - t) * (1 - t) * 110 + 2 * (1 - t) * t * 250 + t * t * 480,
      y: (1 - t) * (1 - t) * 380 + 2 * (1 - t) * t * 110 + t * t * -70,
    });
    const pos = at(e); const ahead = at(Math.min(1, e + 0.02));
    const ang = Math.atan2(ahead.y - pos.y, ahead.x - pos.x);
    const alpha = k > 0.72 ? 1 - (k - 0.72) / 0.28 : 1;
    c.save(); c.globalAlpha = Math.max(0, alpha);
    for (let i = 5; i >= 1; i--) { const t = at(Math.max(0, e - i * 0.035)); p.circle(t.x, t.y, Math.max(1, 3.2 - i * 0.45), C.mute); }
    p.plane(pos.x, pos.y, ang, 1.5, C.ink);
    c.restore();
  }
  /** 结果页彩纸：从画作背后散落的小星星与圆点（先画、画作后画，所以永远不会遮住画面）。 */
  confetti(age: number, cx: number, top: number) {
    const p = this.paint; const c = p.ctx; const t = age / 1000;
    const rnd = (i: number, salt: number) => { const v = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453; return v - Math.floor(v); };
    for (let i = 0; i < 26; i++) {
      const x = cx + (rnd(i, 1) - 0.5) * 250 + (rnd(i, 3) - 0.5) * 50 * t + Math.sin(age / 300 + i) * 7;
      const y = top + rnd(i, 2) * 30 + 150 * t * t + 40 * t;
      c.save(); c.globalAlpha = Math.max(0, 1 - age / 1600);
      const color = [C.gold, C.orange, C.accent, C.blue][i % 4];
      if (i % 3) p.circle(x, y, 2 + rnd(i, 4) * 2.4, color); else p.star(x, y, 3 + rnd(i, 4) * 3, color);
      c.restore();
    }
  }
  /** 结果页邮戳：双环 + 四字批注 + 小星；由结果页以盖章动画画在画作右下角。 */
  resultStamp(cx: number, cy: number, r: number, text: string, color: string) {
    const p = this.paint; const c = p.ctx;
    c.strokeStyle = color; c.lineWidth = 2.4;
    c.beginPath(); c.arc(cx, cy, r, 0, Math.PI * 2); c.stroke();
    c.lineWidth = 1.2; c.beginPath(); c.arc(cx, cy, r * 0.74, 0, Math.PI * 2); c.stroke();
    p.text(text, cx, cy - 2, r * 0.34, color, '700', 'center');
    p.star(cx, cy + r * 0.4, r * 0.15, color);
  }
  render(now: number) {
    if(this.screen!=='shop'&&this.screen!=='brush-book')this.studio.picker.close();
    const view = this.layout; const scale = this.platform.dpr * view.scale;
    this.ctx.setTransform(this.platform.dpr, 0, 0, this.platform.dpr, 0, 0);
    this.paint.background(this.platform.width, this.platform.height);
    this.ctx.setTransform(scale, 0, 0, scale, view.left * this.platform.dpr, 0);
    this.paint.width = this.W; this.paint.tablet = this.tablet;
    this.ctx.translate(0, this.top); this.paint.hits = [];
    switch (this.screen) {
      case 'flight': this.flight.render(now); break;
      case 'modes': this.modeScreen(); break;
      case 'rescue': this.rescue.render(now); break;
      case 'lobby': this.live.render(now); this.paint.button('lobby-flight','涂鸦战机 →',this.W-144,55,120,32,()=>void this.flight.lobby(),'secondary'); break;
      case 'live-create': case 'live-room': this.live.render(now); break;
      case 'welcome': this.welcome(); break;
      case 'home': this.homeScreen(); break;
      case 'session': this.drawGuess.sessionScreen(); break;
      case 'draw': this.drawGuess.drawingScreen(); break;
      case 'guess': this.drawGuess.guessScreen(now); break;
      case 'result': this.drawGuess.resultScreen(); break;
      case 'replay': this.drawGuess.replayScreen(now); break;
      case 'settings': this.settingsScreen(); break;
      case 'settings-profile': this.settingsProfileScreen(); break;
      case 'settings-redeem': this.settingsRedeemScreen(); break;
      case 'drawing-examples': this.examples.listScreen(); break;
      case 'drawing-example': this.examples.playerScreen(now); break;
      case 'shop': this.studio.renderShop(); break;
      case 'brush-book': this.studio.render(); break;
      case 'gallery': this.gallery.screen(); break;
      case 'artwork': this.gallery.artworkScreen(now); break;
      case 'privacy': this.privacyScreen(); break;
    }
    // 星星余额变化跟踪：任何页面上的星星罐都读这两个字段做弹跳。
    const coins = this.player?.coins;
    if (coins !== undefined) { if (this.coinsShown >= 0 && coins !== this.coinsShown) this.coinsAt = Date.now(); this.coinsShown = coins; }
    this.wipeOverlay(now);
    this.mailPlane(now);
    if (this.busy) this.busyBubble(now);
    if (Date.now() < this.toastUntil) this.toastBubble();
    this.ctx.globalAlpha = 1;
    this.platform.controls(this.paint.hits.map(h => ({ ...h, ...deviceRect(h, view), onScroll: h.onScroll ? (delta: number) => h.onScroll!(delta / view.scale) : undefined, disabled: h.disabled || this.busy })), this.screen);
  }
  /** 底部导航出现的页面：忙碌 / toast 浮层在这些页面贴着导航上方错层排布。 */
  hasNav() { return ['home', 'lobby', 'gallery', 'shop', 'settings'].includes(this.screen); }
  busyBubble(now: number) {
    const p = this.paint; const c = this.ctx; const label = '正在整理画纸…';
    c.font = `600 ${p.fontSize(12)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
    const w = Math.ceil(c.measureText(label).width) + 62;
    const x = Math.round(this.W / 2 - w / 2); const h = 38;
    // 有底部导航的页面贴着导航上方（矮屏也不会压到页面中部内容）；无导航的流程页保持原位（toast 之上再留一层）。
    const y = this.H - this.top - (this.hasNav() ? 122 : 176);
    p.box(x, y, w, h, C.cream, 19, undefined, 14);
    ([C.accent, C.gold, C.orange] as const).forEach((color, i) => {
      const phase = ((now / 420) + i * 0.9) % 2.7;
      const lift = phase < 1 ? Math.sin(phase * Math.PI) * 3.5 : 0;
      p.circle(x + 20 + i * 9, y + h / 2 - lift, 2.4, color);
    });
    p.text(label, x + 46, y + h / 2 + 0.5, T.caption, C.ink, '600');
  }
  toastBubble() {
    const p = this.paint; const c = this.ctx;
    const size = 13; const lineW = 246; const padX = 16; const badge = 26; const gap = 10;
    c.font = `500 ${p.fontSize(size)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
    const lines: string[] = []; let line = '';
    for (const char of this.toastText) {
      if (line && c.measureText(line + char).width > lineW) { lines.push(line); line = char; } else line += char;
    }
    if (line) lines.push(line);
    if (lines.length > 2) {
      const rest = lines.slice(1).join(''); lines.length = 1; let tail = '';
      for (const char of rest) { if (c.measureText(tail + char + '…').width > lineW) break; tail += char; }
      lines.push(tail + '…');
    }
    const widest = Math.max(...lines.map(l => c.measureText(l).width));
    const w = Math.min(350, Math.max(150, Math.ceil(widest) + padX * 2 + badge + gap));
    const h = lines.length > 1 ? 22 + lines.length * 20 : 44;
    const x = Math.round(this.W / 2 - w / 2); let y = this.H - this.top - 84 - h;
    // 忙碌浮层在导航页贴在导航上方（同一竖槽）：toast 与它同时出现时向上错开一层，互不遮挡。
    if (this.busy && this.hasNav()) y -= 48;
    const remaining = this.toastUntil - Date.now(); const shown = 3500 - remaining;
    const fade = Math.min(1, shown / 160, remaining / 280);
    const slide = (1 - Math.min(1, shown / 160)) * 8;
    c.save(); c.globalAlpha = Math.max(0, fade);
    p.box(x, y + slide, w, h, C.cream, h / 2, undefined, 16);
    const bx = x + padX + badge / 2; const by = y + slide + h / 2;
    c.save(); c.shadowColor = 'rgba(34, 116, 184, 0.30)'; c.shadowBlur = 6; c.shadowOffsetY = 2;
    const g = c.createLinearGradient(0, by - badge / 2, 0, by + badge / 2);
    g.addColorStop(0, '#4DA8E4'); g.addColorStop(1, '#1F6CAE');
    c.beginPath(); c.arc(bx, by, badge / 2, 0, Math.PI * 2); c.fillStyle = g; c.fill(); c.restore();
    p.icon('plane', bx, by + 0.5, 14, C.white);
    const tx = x + padX + badge + gap; const lh = 20; const ty = y + slide + h / 2 - ((lines.length - 1) * lh) / 2;
    lines.forEach((l, i) => p.text(l, tx, ty + i * lh + 0.5, size, C.ink, '500'));
    c.restore();
  }
  welcome() {
    const p = this.paint; const h = this.H - this.top; const now = Date.now();
    this.ambient(now);
    const compact = h < 690; const heroY = compact ? 100 : 118; const heroH = Math.max(90, Math.min(this.tablet ? 280 : compact ? 190 : 231, h - 460));
    const greetingY = heroY + heroH + 35; const paragraphY = greetingY + 90;
    p.title('友友画', p.x(30), 50, T.brand);
    p.tracking('DRAW A LITTLE CLOSER', p.x(31), 86, 10, C.mute, '600', 1.5);
    const hero = p.ctx.createLinearGradient(0, heroY, 0, heroY + heroH);
    hero.addColorStop(0, '#E3F0FA'); hero.addColorStop(1, '#FBE9D8');
    p.box(p.x(26), heroY, p.span(338), heroH, hero, 30, undefined, 22);
    p.circle(p.x(78), heroY + 34, 24, '#FFFFFF70'); p.circle(p.x(322), heroY + heroH - 38, 32, '#FFFFFF50');
    // 一封正在打开的信：信纸以缓慢节奏轻轻探出信封。
    const peek = 0.55 + 0.1 * Math.sin(now / 750);
    p.envelope(p.x(195), heroY + heroH / 2 + 14 + Math.sin(now / 900) * 2, compact ? 166 : 188, compact ? 104 : 118, { letter: peek });
    p.postmark(p.x(310), heroY + 54, 25, '友友', '邮局');
    // 标语逐行淡入（进场打字机感），之后恒定显示。
    const age = now - this.bootAt;
    const fadeIn = (delay: number) => Math.max(0, Math.min(1, (age - delay) / 500));
    p.ctx.save();
    p.ctx.globalAlpha = fadeIn(150); p.text('画得像不像，', p.x(29), greetingY, T.display, C.ink, '700');
    p.ctx.globalAlpha = fadeIn(500); p.text('都想给你看。', p.x(29), greetingY + 38, T.display, C.ink, '700');
    p.ctx.globalAlpha = fadeIn(800); p.wrap('选一个词，留下一幅画。朋友有空时猜一猜，下一张画纸就轮到 TA。', p.x(31), paragraphY, p.span(315), T.body, C.mute, 24);
    p.ctx.restore();
    const y = Math.max(paragraphY + 85, h - 172);
    p.button('start', this.consented ? '继续我的友友来信  →' : '同意并开始创作  →', p.x(26), y, p.span(338), 52, () => { void this.signIn(); });
    p.button('practice', '先随手画画', p.x(26), y + 64, p.span(164), 40, () => this.drawGuess.draw(true), 'secondary');
    p.button('privacy', '隐私与游玩说明', p.x(200), y + 64, p.span(164), 40, () => { this.go('privacy'); }, 'plain');
    const offline = this.platform.get<{player:Player;token:string}|null>('flight-account',null);
    if(offline)p.button('offline-flight','离线进入涂鸦战机',p.x(26),y+110,p.span(338),32,()=>{this.player=offline.player;this.token=offline.token;this.drawingSettings.bind();void this.flight.open();},'plain');
    else p.text(__VERSION__, p.x(195), y + 124, T.note, C.mute, '400', 'center');
  }
  homeScreen() {
    const p = this.paint; const now = Date.now();
    this.ambient(now);
    this.header('友友画', '一笔一画，都有回音。', false);
    // iPad 缩放坐标等低可用高度（H-top < 620）走紧凑 hero：矮 hero + 省略装饰，给信件列表留出空间（至少两封信）。
    const shortHome = this.H - this.top < 620;
    const heroH = this.tablet ? 156 : shortHome ? 100 : 172;
    const hero = p.ctx.createLinearGradient(0, 106, 0, 106 + heroH);
    hero.addColorStop(0, '#E3F0FA'); hero.addColorStop(1, '#FBE9D8');
    p.box(p.x(24), 106, p.span(342), heroH, hero, 26, undefined, 20);
    p.text('今天，画点什么？', p.x(44), 140, shortHome ? 20 : 23, C.ink, '700');
    if (!shortHome) {
      p.wrap('把脑海里的小想法，\n寄给一个在意的人。', p.x(44), 178, p.span(173), T.sub, C.accentDeep, 23);
      p.envelope(p.x(298), 196 + Math.sin(now / 900) * 2, 106, 70, { letter: 0.45 + 0.12 * Math.sin(now / 800) });
    }
    const jarY = shortHome ? 106 + heroH - 38 : 231;
    const jarW = this.starJar(44, jarY);
    p.text(`${this.player!.solved} 次猜中心意`, 44 + jarW + 14, jarY + 15, T.note, C.mute);
    const buttonsY = 106 + heroH + 18;
    p.button('new', '＋ 开启新的创作', p.x(24), buttonsY, p.span(209), 48, () => this.go('modes'));
    p.button('join', '输入邀请码', p.x(245), buttonsY, p.span(121), 48, () => { void this.run(async () => { const value = await this.platform.prompt('接住好友的邀请', '输入 12 位邀请码'); if (value) await this.accept(value); }); }, 'secondary');
    const listTitleY = buttonsY + (shortHome ? 68 : 81);
    const listY = buttonsY + (shortHome ? 92 : 108);
    p.text('友信', p.x(25), listTitleY, T.section, C.ink, '600');
    p.button('refresh', '刷新', p.x(309), listTitleY - 18, 57, 32, () => { void this.run(() => this.refresh()); }, 'plain');
    const visible = this.sessions.filter(s => s.status !== 'closed');
    // 容量按真实剩余高度计算：卡片排与分页条都必须完整落在底部导航之上。
    const navTop = this.H - this.top - 74;
    const fit = (paged: boolean) => Math.max(1, Math.floor((navTop - listY - (paged ? 110 : 72)) / 75) + 1);
    let capacity = this.tablet ? Math.max(2, Math.floor((navTop - listY - 58) / 120) * 2) : fit(false);
    if (!this.tablet && visible.length > capacity) capacity = fit(true);
    this.page = Math.min(this.page, Math.max(0, Math.ceil(visible.length / capacity) - 1));
    if (!visible.length) {
      const emptyH = Math.max(96, Math.min(129, navTop - listY - 12));
      p.card(p.x(24), listY, p.span(342), emptyH, 22);
      p.mailbox(p.x(84), listY + emptyH / 2 + 6, 0.6);
      p.text('第一张画纸，等你寄出', p.x(218), listY + emptyH / 2 - 12, T.cardStrong, C.ink, '600', 'center');
      p.text('邀请一位朋友，开启一段共同创作。', p.x(218), listY + emptyH / 2 + 18, T.caption, C.mute, '400', 'center');
    }
    visible.slice(this.page * capacity, (this.page + 1) * capacity).forEach((s, i) => {
      if (this.tablet) {
        const width = (this.W - 64) / 2, x = 24 + i % 2 * (width + 16), y = listY + Math.floor(i / 2) * 120;
        p.card(x, y, width, 104, 18);
        p.stamp(x + 16, y + 14, 44, 52);
        const avatar = s.partnerAvatarUrl ? this.partnerAvatarImages.get(s.partnerAvatarUrl) : undefined;
        if (avatar) p.ctx.drawImage(avatar, x + 22, y + 20, 32, 40);
        else p.text(s.partner.slice(0, 1), x + 38, y + 40, T.cardStrong, C.accentDeep, '600', 'center');
        p.fit(s.partner, x + 76, y + 30, width - 94, T.section, C.ink, '600');
        const mode = modeInfo(s.mode);
        p.fit(`${mode.name} · ${s.progressLabel ?? ('第 ' + s.number + ' 回合')}`, x + 16, y + 84, width - 144, T.caption, C.mute);
        const labels = { draw:'轮到我画',guess:'轮到我猜',wait:'等待好友',review:'整理画作',closed:'已结束',record:'轮到我画',receive:'轮到我接',replay:'查看回放' };
        p.button(`session-${s.id}`, labels[s.action], x + width - 120, y + 52, 104, 44, () => this.openSession(s.id), ['guess','receive'].includes(s.action) ? 'orange' : 'secondary');
        return;
      }
      const y = listY + i * 75; p.card(p.x(24), y, p.span(342), 66, 20);
      const avatar = s.partnerAvatarUrl ? this.partnerAvatarImages.get(s.partnerAvatarUrl) : undefined;
      if (s.partnerOnline) { p.circle(p.x(58), y + 33, 24, 'rgba(62, 155, 220, 0.15)'); p.circle(p.x(58), y + 33, 21, 'rgba(62, 155, 220, 0.25)'); }
      // 齿孔邮票里的好友头像：每一封信都贴着 TA 的“邮票”。
      p.stamp(p.x(38), y + 9, 40, 48);
      if (avatar) {
        p.ctx.save(); p.ctx.beginPath(); p.ctx.rect(44, y + 15, 28, 36); p.ctx.clip(); p.ctx.drawImage(avatar, 44, y + 15, 28, 36); p.ctx.restore();
      } else {
        p.text(s.partner.slice(0, 1) || '友', p.x(58), y + 34, T.cardStrong, C.accentDeep, '700', 'center');
      }
      p.fit(s.partner, p.x(92), y + 21, p.span(168), T.body, C.ink, '600');
      const mode = modeInfo(s.mode); p.box(p.x(92), y + 33, 61, 20, mode.tint, 7);
      p.text(mode.name, p.x(122.5), y + 43, 10, mode.color, '600', 'center');
      p.fit(s.progressLabel ?? ('第 ' + s.number + ' 回合'), p.x(159), y + 44, p.span(106), T.note, C.mute);
      const labels = { draw: '轮到我画', guess: '轮到我猜', wait: '等待好友', review: '整理画作', closed: '已结束', record: '轮到我画', receive: '轮到我接', replay: '查看回放' };
      p.button(`session-${s.id}`, labels[s.action], p.x(273), y + 16, p.span(80), 34, () => this.openSession(s.id), ['guess', 'receive'].includes(s.action) ? 'orange' : 'secondary');
      // 轮到我猜的信封盖着朱砂火漆：未拆的信一眼可辨。
      if (s.action === 'guess' || s.action === 'receive') p.seal(p.x(356), y + 7, 7.5);
    });
    if (visible.length > capacity) this.paging(visible.length, capacity, this.H - this.top - 112);
    this.navigation('home');
  }
  paging(total: number, capacity: number, y: number) {
    const pages = Math.ceil(total / capacity); this.page = Math.min(this.page, pages - 1);
    this.paint.button('prev', '‹ 上一页', this.paint.x(25), y, this.paint.span(85), 32, () => this.page--, 'plain', this.page <= 0);
    this.paint.text(`${this.page + 1} / ${pages}`, this.paint.x(195), y + 16, 12, C.mute, '400', 'center');
    this.paint.button('next', '下一页 ›', this.paint.x(280), y, this.paint.span(85), 32, () => this.page++, 'plain', this.page + 1 >= pages);
  }
  settingsScreen() {
    const p = this.paint; const h = this.H - this.top;
    this.header('设置', '按你喜欢的方式，慢慢画。', false);
    // 页头右侧的装饰小邮戳（x≤244，避开右上角胶囊遮挡区）。
    p.postmark(p.x(228), 30, 16, '档案');
    // 档案夹式分组列表：资料大卡 + 「偏好」「更多」两张分组卡片，行距与间距随视口收放；
    // 详细操作收进「个人资料」二级页，超矮屏（含 iPad 缩放坐标）省略分组标题与版本行。
    const tiny = h < 640; const compact = h < 720;
    const profileH = tiny ? 70 : compact ? 80 : 104;
    const gap = tiny ? 4 : compact ? 8 : 18;
    let y = tiny ? 86 : compact ? 92 : 106;
    const rowH = Math.min(tiny ? 34 : 52, Math.floor((h - y - profileH - gap * 3 - (tiny ? 0 : 68) - 86) / 6));
    // 寄件人名片：邮票头像 + 昵称 + 默契数，整卡可点进入「个人资料」二级页。
    p.card(p.x(24), y, p.span(342), profileH, 22);
    this.loadAvatar(this.player!.avatarUrl);
    const cy = y + profileH / 2;
    p.stamp(p.x(40), cy - 29, 48, 58);
    if (this.avatarImage) { p.ctx.save(); p.ctx.beginPath(); p.ctx.rect(p.x(40) + 6, cy - 23, 36, 46); p.ctx.clip(); p.ctx.drawImage(this.avatarImage, p.x(40) + 6, cy - 23, 36, 46); p.ctx.restore(); }
    else p.text(this.player!.nickname.slice(0, 1), p.x(40) + 24, cy + 1, T.glyph, C.accentDeep, '700', 'center');
    p.fit(this.player!.nickname, p.x(104), cy - 10, p.span(226), T.section, C.ink, '700');
    p.text(`已经读懂 ${this.player!.solved} 次小心思`, p.x(104), cy + 14, T.caption, C.mute);
    p.text('›', p.x(345), cy, T.cardTitle, C.mute, '400', 'center');
    p.hits.push({ id: 'settings-profile', label: '个人资料', x: p.x(24), y, w: p.span(342), h: profileH, action: () => { this.go('settings-profile'); } });
    y += profileH + gap;
    if (!tiny) { p.text('偏好', p.x(42), y + 6, T.caption, C.mute, '600'); y += 22; }
    this.settingsCard(y, rowH, [
      { id: 'vibration', label: '猜中时的轻振动', toggle: this.platform.get('vibration', true), action: () => this.platform.set('vibration', !this.platform.get('vibration', true)) },
    ]);
    y += rowH + gap;
    if (!tiny) { p.text('更多', p.x(42), y + 6, T.caption, C.mute, '600'); y += 22; }
    const moreRows = [
      { id: 'settings-redeem', label: '使用兑换码', action: () => { this.go('settings-redeem'); } },
      { id: 'settings-brush-guide', label: '画布工具说明', action: () => { void this.run(async () => { await this.platform.confirm('画布小指南', this.drawGuess.guideText()); }); } },
      { id: 'settings-examples', label: '画画例子', value: `${this.examples.count} 例 · 3 级教程`, action: () => this.examples.openList() },
      { id: 'settings-practice', label: '随手画画', action: () => this.drawGuess.draw(true) },
      { id: 'settings-privacy', label: '隐私与游玩说明', action: () => { this.go('privacy'); } },
    ];
    this.settingsCard(y, rowH, moreRows); y += rowH * moreRows.length + gap;
    if (h >= 640) p.text(`友友画 · ${__VERSION__}`, p.x(195), y + 8, T.note, C.mute, '400', 'center');
    this.navigation('settings');
  }
  settingsRedeemScreen() {
    const p = this.paint; this.header('使用兑换码', '输入兑换码，奖励会立即加入你的星星。');
    p.card(p.x(24), 112, p.span(342), 170, 22); p.tape(p.x(195), 114, 0.02, 56);
    p.text('兑换码', p.x(46), 150, T.body, C.mute); p.box(p.x(46), 166, p.span(298), 48, C.paper, 14, C.line, 1);
    p.text(this.redeemCode || '点击输入兑换码', p.x(62), 191, 15, this.redeemCode ? C.ink : C.mute);
    p.hits.push({ id: 'redeem-input', label: '输入兑换码', x: p.x(46), y: 166, w: p.span(298), h: 48, action: () => { void this.run(async () => { const code = await this.platform.prompt('输入兑换码', '请输入兑换码', this.redeemCode); if (code?.trim()) this.redeemCode = code.trim(); }); } });
    p.button('redeem-submit', '兑换', p.x(46), 228, p.span(298), 42, () => { void this.run(async () => { if (!this.redeemCode) { this.toast('请先输入兑换码。'); return; } const value = await this.api<{reward:number; player:Player}>('/redeem', 'POST', { code: this.redeemCode }, requestId()); this.player = value.player; this.redeemCode = ''; this.toast(`兑换成功，获得 ${value.reward} 颗星星。`); }); }, 'primary');
  }
  /** 设置分组卡片：白卡内逐行排列、行间缝线分隔；行可带「值 + ›」入口或滑动开关（整行可点）。 */
  settingsCard(y: number, rowH: number, rows: { id: string; label: string; value?: string; toggle?: boolean; action: () => void }[]) {
    const p = this.paint;
    p.card(p.x(24), y, p.span(342), rowH * rows.length, 20);
    rows.forEach((row, i) => {
      const ry = y + i * rowH; const cy = ry + rowH / 2;
      if (i > 0) p.dashed(p.x(46), ry, p.x(344), ry, C.line, 1, [3, 4]);
      p.text(row.label, p.x(46), cy, T.body, C.ink, '600');
      if (row.toggle !== undefined) {
        p.box(p.x(316), cy - 13, 46, 26, row.toggle ? C.accent : C.line, 13);
        p.circle(p.x(316) + (row.toggle ? 33 : 13), cy, 11, C.white, undefined, 3);
      } else {
        if (row.value) p.text(row.value, p.x(322), cy, T.caption, C.mute, '400', 'right');
        p.text('›', p.x(345), cy, T.cardTitle, C.mute, '400', 'center');
      }
      p.hits.push({ id: row.id, label: row.toggle !== undefined ? `${row.label} ${row.toggle ? '已开启' : '已关闭'}` : row.label, x: p.x(24), y: ry, w: p.span(342), h: rowH, action: row.action });
    });
  }
  /** 二级页：个人资料（昵称 / 头像 / 微信资料同步 / 删除账号），返回键回到设置主页。 */
  settingsProfileScreen() {
    const p = this.paint; const h = this.H - this.top; const compact = h < 640;
    this.header('个人资料', '头像和昵称会展示给和你一起画的友友。');
    const cardY = compact ? 92 : 106; const avatarR = compact ? 31 : 37; const cardH = compact ? 148 : 168;
    p.card(p.x(24), cardY, p.span(342), cardH, 24); p.tape(p.x(195), cardY + 2, 0.02, 56);
    this.loadAvatar(this.player!.avatarUrl);
    const ay = cardY + (compact ? 42 : 50);
    // 大头像装进齿孔邮票：这就是你在信封上的样子。
    const stampW = avatarR * 2 + 14; const stampH = avatarR * 2 + 22;
    p.stamp(this.W / 2 - stampW / 2, ay - stampH / 2 + 4, stampW, stampH);
    if (this.avatarImage) { p.ctx.save(); p.ctx.beginPath(); p.ctx.rect(this.W / 2 - stampW / 2 + 7, ay - stampH / 2 + 11, stampW - 14, stampH - 14); p.ctx.clip(); p.ctx.drawImage(this.avatarImage, this.W / 2 - stampW / 2 + 7, ay - stampH / 2 + 11, stampW - 14, stampH - 14); p.ctx.restore(); }
    else p.text(this.player!.nickname.slice(0, 1), p.x(195), ay + 5, T.logo, C.accentDeep, '700', 'center');
    p.fit(this.player!.nickname, p.x(195), cardY + (compact ? 96 : 108), p.span(250), T.cardTitle, C.ink, '700', 'center');
    p.text(`已经读懂 ${this.player!.solved} 次小心思`, p.x(195), cardY + (compact ? 118 : 132), T.caption, C.mute, '400', 'center');
    const btnH = compact ? 42 : 46; const step = btnH + 10;
    let y = cardY + cardH + (compact ? 12 : 16);
    p.button('edit-profile', '修改昵称', p.x(25), y, p.span(340), btnH, () => { void this.run(async () => {
      const nickname = await this.platform.prompt('自定义昵称', '1～24 个字符', this.player!.nickname); if (!nickname?.trim()) return;
      this.player = await this.api<Player>('/me/profile', 'POST', { nickname: nickname.trim() }, requestId());
      this.toast('昵称已更新。');
    }); }, 'secondary');
    p.button('edit-avatar', '选择头像', p.x(25), y + step, p.span(340), btnH, () => { void this.run(async () => {
      const avatar = await this.platform.chooseAvatar?.();
      if (!avatar) { this.toast('请选择一张 PNG 或 JPG 图片。'); return; }
      this.player = await this.api<Player>('/me/profile', 'POST', { avatar }, requestId());
      this.toast('头像已更新。');
    }); }, 'secondary');
    p.button('wechat-profile', '同步微信头像和昵称', p.x(25), y + step * 2, p.span(340), btnH, () => { this.toast('请点击按钮授权微信资料。'); }, 'primary', false, undefined, (profile, error) => {
      if (!profile) { this.toast(error?.message ?? '未完成微信资料授权。'); return; }
      void this.run(async () => {
        this.player = await this.api<Player>('/me/profile', 'POST', { nickname: profile.nickname }, requestId());
        if (this.platform.prepareWeChatAvatar) {
          const avatar = await this.platform.prepareWeChatAvatar(profile.avatarUrl);
          this.player = await this.api<Player>('/me/profile', 'POST', { avatar }, requestId());
        }
        this.toast('微信头像和昵称已更新。');
      });
    });
    y += step * 3 + (compact ? 4 : 10);
    const dangerH = compact ? 38 : 42; const dangerY = Math.min(y, h - dangerH - 16);
    p.box(p.x(25), dangerY, p.span(340), dangerH, C.white, dangerH / 2, '#F3D9D4');
    p.text('删除我的资料', p.x(195), dangerY + dangerH / 2, T.sub, C.red, '600', 'center');
    p.hits.push({ id: 'delete-account', label: '删除我的资料', x: p.x(25), y: dangerY, w: p.span(340), h: dangerH, action: () => { void this.run(async () => {
      if (!await this.platform.confirm('删除全部游戏资料？', '你的账号、作品、星星和双方共享的友友会话会被永久删除。这项操作不会保留在游戏里。')) return;
      await this.api('/me', 'DELETE'); await this.flight.dispose(); await this.platform.durable?.clear();this.flight=new FlightClient(this); this.live.clear(); this.drawGuess.dispose(); this.platform.clearAccount(); this.player = null; this.drawingSettings.resetAccount(); this.token = ''; this.consented = false; this.paletteSelection.id = 'starter'; this.drawGuess = new DrawGuessClient(this); this.drawingSettings.register(this.drawGuess,this.live,this.flight); this.drawingSettings.bind(); this.rescue.clear(); this.gallery = new GalleryClient(this); this.screen = 'welcome'; this.toast('游戏资料已删除。');
    }); } });
  }
  /** 色包购买只锁购买按钮，实时房间继续收包和计时。 */
  private async useShopPalette(action: () => Promise<void>) {
    if (this.paletteBuying) return;
    this.paletteBuying = true;
    try { await action(); } catch (error) {
      if (error instanceof ApiError && error.status === 401) { this.token = ''; this.player = null; this.screen = 'welcome'; }
      this.toast(error instanceof Error ? error.message : '色包暂时无法使用，请重试。');
    }
    finally { this.paletteBuying = false; }
  }
  shopScreen() { this.studio.renderShop(); }
  privacyScreen() {
    const p = this.paint; this.header('隐私与游玩说明');
    let y = 110;
    const sections = [
      ['只为友友之间的默契', '登录后保存游戏身份、回合进度、作品、操作回放及星星，用于跨时间继续共同创作。无需位置、通讯录或麦克风权限。'],
      ['一张画纸，两个人看', '会话作品仅对参与者开放，运营人员在内容审核或处理举报时可查看。分享邀请不包含答案。请勿绘制个人敏感资料。'],
      ['把画画当成一封信', '请用图画表达题目，避免直接写答案。收到不适当内容，可在会话管理中举报或屏蔽友友。'],
      ['随时保存，也可告别', '随手画草稿保存在当前设备。已完成作品可保存为图片；在设置中删除资料会同时删除与好友共享的会话和作品。'],
    ];
    for (const [title, text] of sections.slice(this.privacyPage * 2, this.privacyPage * 2 + 2)) { p.text(title, p.x(27), y, T.section, C.ink, '600'); y = p.wrap(text, p.x(27), y + 33, p.span(332), T.sub, C.mute, 23) + 30; }
    p.postmark(p.x(195), 453, 20, `${this.privacyPage + 1} / 2`, '');
    p.button('privacy-page', this.privacyPage === 0 ? '继续阅读 →' : '← 上一页', p.x(25), 487, p.span(340), 43, () => { this.privacyPage = 1 - this.privacyPage; }, 'secondary');
    p.button('privacy-return', '我知道了', p.x(25), this.H - this.top - 85, p.span(340), 48, () => { this.screen = this.player ? 'settings' : 'welcome'; });
  }
  constructor(public platform: Platform) {
    this.ctx = platform.canvas.getContext('2d')!; this.paint = new Painter(this.ctx);
    this.paint.setTexture(platform.createCanvas(256, 256));
    this.drawGuess = new DrawGuessClient(this); this.rescue = new RescueClient(this); this.live = new LiveClient(this); this.gallery = new GalleryClient(this);
    this.flight = new FlightClient(this);
    this.examples = new DrawingExamplesClient(this);
    this.drawingSettings = new DrawingPreferencesClient(this); this.studio=new BrushStudio(this);
    this.drawingSettings.register(this.drawGuess,this.live,this.flight); this.drawingSettings.bind();
    this.pendingInvite = platform.initialInvite;
    this.pendingRoom = platform.initialRoom;
    platform.onRoomInvite?.(code => { this.pendingRoom = code; });
    this.consented = platform.get('consent-v1', false);
    platform.onResize?.(() => this.resize());
    platform.onPointer((kind, p) => this.pointer(kind, p));
    platform.onPause(() => { this.stopped = true; this.refreshGeneration++; this.studio.shopScroll.cancel();this.studio.picker.close(); this.drawGuess.pause(); this.rescue.pause(); this.live.pause(); this.examples.pause(); this.flight.pause(); this.paint.pressedId = null; });
    platform.onResume(invite => {
      this.stopped = false; this.drawGuess.resume(); this.live.resume(); void this.drawingSettings.pull(); if (invite) this.pendingInvite = invite;
      if (this.player) {
        if (this.pendingRoom || this.pendingInvite) void this.run(async () => { if (this.pendingRoom) { const code = this.pendingRoom; this.pendingRoom = undefined; await this.joinRoom(code); } else if (this.pendingInvite) await this.accept(this.pendingInvite); });
        else void this.pollRefresh();
      }
      else if (this.consented) void this.signIn();
    });
    requestAnimationFrame(time => this.frame(time));
    if (this.consented) { const cached=platform.get<{player:Player;token:string}|null>('flight-account',null); if(cached&&platform.get('flight-return',false)) {this.player=cached.player;this.token=cached.token;this.drawingSettings.bind();void this.flight.open();} else void this.signIn(); }
  }

  resize() {
    // A window change is not a navigation/background event: keep room and playback clocks.
    this.flight.resize(); this.drawGuess.resize(); this.live.resize(); this.rescue.resize(); this.studio.resize(); this.gallery.resize(); this.examples.resize();
    this.paint.pressedId = null;
  }
  pointer(kind: 'down' | 'move' | 'up' | 'cancel', point: Pointer) {
    if (kind === 'down' && this.screen === 'flight') this.platform.unlockAudio?.();
    point = localPointer(point, this.layout);
    if (kind === 'up' || kind === 'cancel') this.paint.pressedId = null;
    if (this.studio.picker.pointer(kind,point)) return;
    if (this.studio.shopPointer(kind,point)) return;
    if (this.screen === 'brush-book' && this.studio.pointer(kind,point)) return;
    if (this.screen === 'draw') { this.drawGuess.pointer(kind, point); return; }
    if (this.screen === 'rescue' && this.rescue.pointer(kind, point)) return;
    if (this.screen === 'live-room' && this.live.pointer(kind, point)) return;
    if (this.screen === 'flight' && this.flight.pointer(kind, point)) return;
    // 作品详情页：进度轨拖动 / 画作点按与滑动手势先走画廊自己的指针管线，剩下的落回按钮命中。
    if (this.screen === 'artwork' && this.gallery.pointer(kind, point)) return;
    if (kind !== 'down' || this.busy) return;
    const { x, y } = point;
    const hit = [...this.paint.hits].reverse().find(h => !h.disabled && x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h);
    if (hit) { if (hit.id === "mode-doodle-flight") this.platform.unlockAudio?.(); this.paint.pressedId = hit.id; hit.action(); }
  }
  modeScreen() {
    this.header('开启新的创作', '选一种玩法，和好友一起留下新的回忆。');
    const h = this.H - this.top; const cardH = Math.min(148, (h - 141) / GAME_MODES.length - 12);
    GAME_MODES.forEach((mode, i) => {
      const y = 103 + i * (cardH + 12); this.paint.card(this.paint.x(24), y, this.paint.span(342), cardH, 8);
      this.paint.circle(this.paint.x(61), y + 32, 20, mode.tint); this.paint.icon(mode.icon, this.paint.x(61), y + 32, 20, mode.color);
      this.paint.text(mode.name, this.paint.x(96), y + 25, T.cardTitle, C.ink, '700');
      this.paint.fit(mode.description, this.paint.x(96), y + 48, this.paint.span(247), T.caption, C.mute);
      this.paint.button('mode-' + mode.id, '去' + mode.name, this.paint.x(42), y + cardH - 43, this.paint.span(306), 34, () => {
        if (mode.id === 'relay-rescue') this.rescue.intro();
        else if (mode.id === 'doodle-flight') void this.flight.open();
        else if (mode.id === 'live-draw') this.live.createScreen();
        else void this.run(async () => this.openView(await this.api<SessionView>('/sessions', 'POST', { mode: mode.id })));
      }, 'secondary');
    });
  }
}
